import { useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, Button, Badge, Callout, Spinner, Modal, CopyField, TextField, Gauge, Table, type Column } from "@/design/components";
import { ApiError } from "@/api/client";
import {
  getOperatorOrg,
  setOrgStatus,
  setOrgTier,
  refundCharge,
  cancelSubscription,
  releaseTierToBilling,
  impersonateOrg,
  listOperatorOrgAgents,
  listOperatorOrgChannels,
  type OrgTier,
  type OperatorChannel,
  type RefundResult,
  type CancelSubscriptionResult,
  type ImpersonationResult,
} from "@/api/operator/orgs";
import { AgentList } from "@/features/agents/AgentList";
import { TIER_LABEL } from "@/features/onboarding/tiers";
import styles from "./OperatorOrgDetailScreen.module.css";

const apiMessage = (err: unknown, fallback: string): string => (err instanceof ApiError ? err.message : fallback);
function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
const STATUS_TONE = { active: "success", suspended: "warning", deleted: "danger" } as const;
const TIERS: readonly OrgTier[] = ["free", "small", "medium", "large", "xlarge"];

export function OperatorOrgDetailScreen() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ["operator", "org", id], queryFn: ({ signal }) => getOperatorOrg(id, signal), enabled: id !== "" });
  // Live per-agent metadata for this org (from the relay; degrades to [] server-side).
  const agentsQuery = useQuery({
    queryKey: ["operator", "org", id, "agents"],
    queryFn: ({ signal }) => listOperatorOrgAgents(id, signal),
    enabled: id !== "",
  });
  // The channels behind this org (Tier A rows + Tier B live relay stats).
  const channelsQuery = useQuery({
    queryKey: ["operator", "org", id, "channels"],
    queryFn: ({ signal }) => listOperatorOrgChannels(id, signal),
    enabled: id !== "",
  });

  const status = useMutation({
    mutationFn: (next: "active" | "suspended") => setOrgStatus(id, next),
    onSuccess: (detail) => qc.setQueryData(["operator", "org", id], detail),
  });

  const [refundOpen, setRefundOpen] = useState(false);
  const [refundResult, setRefundResult] = useState<RefundResult | null>(null);
  const refund = useMutation({
    mutationFn: (input: { chargeId: string; amount?: number }) => refundCharge(id, input),
    onSuccess: (r) => setRefundResult(r),
  });

  const [impersonation, setImpersonation] = useState<ImpersonationResult | null>(null);
  const impersonate = useMutation({ mutationFn: () => impersonateOrg(id), onSuccess: (r) => setImpersonation(r) });

  const [tierOpen, setTierOpen] = useState(false);
  const tier = useMutation({
    mutationFn: (input: { tier: OrgTier; note: string }) => setOrgTier(id, input),
    onSuccess: (detail) => {
      qc.setQueryData(["operator", "org", id], detail);
      setTierOpen(false);
    },
  });
  function openTier() {
    tier.reset();
    setTierOpen(true);
  }
  function closeTier() {
    if (tier.isPending) return;
    setTierOpen(false);
  }

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelResult, setCancelResult] = useState<CancelSubscriptionResult | null>(null);
  const cancelSub = useMutation({
    mutationFn: (input: { note: string }) => cancelSubscription(id, input),
    onSuccess: (r) => {
      setCancelResult(r);
      qc.invalidateQueries({ queryKey: ["operator", "org", id] });
    },
  });
  function openCancel() {
    cancelSub.reset();
    setCancelResult(null);
    setCancelOpen(true);
  }
  function closeCancel() {
    if (cancelSub.isPending) return;
    setCancelOpen(false);
  }

  const [releaseOpen, setReleaseOpen] = useState(false);
  const release = useMutation({
    mutationFn: (input: { note: string }) => releaseTierToBilling(id, input),
    onSuccess: (detail) => {
      qc.setQueryData(["operator", "org", id], detail);
      setReleaseOpen(false);
    },
  });
  function openRelease() {
    release.reset();
    setReleaseOpen(true);
  }
  function closeRelease() {
    if (release.isPending) return;
    setReleaseOpen(false);
  }

  function openRefund() {
    refund.reset();
    setRefundResult(null);
    setRefundOpen(true);
  }
  function closeRefund() {
    if (refund.isPending) return;
    setRefundOpen(false);
  }

  const org = query.data;

  // Per-org live rollup, aggregated from the channels' relay stats (idle channels
  // contribute 0 / no activity). Real relay data — no usage_daily dependency.
  const channels = channelsQuery.data ?? [];
  const liveAgents = channels.reduce((n, ch) => n + (ch.live?.agentCount ?? 0), 0);
  const liveMessages = channels.reduce((n, ch) => n + (ch.live?.messageCount ?? 0), 0);
  const lastActivityAt = channels.reduce<string | null>((max, ch) => {
    const t = ch.live?.lastActivityAt ?? null;
    return t && (!max || t > max) ? t : max;
  }, null);

  const channelColumns: Column<OperatorChannel>[] = [
    { key: "name", header: "Channel", render: (ch) => <span className={styles.chanName}>{ch.name}</span> },
    { key: "agents", header: "Agents", align: "end", render: (ch) => <span className={styles.mono}>{ch.live ? ch.live.agentCount : "—"}</span> },
    { key: "messages", header: "Messages", align: "end", render: (ch) => <span className={styles.mono}>{ch.live ? ch.live.messageCount : "—"}</span> },
    { key: "slack", header: "Slack", align: "end", render: (ch) => <span className={styles.mono}>{ch.slackMappings}</span> },
    { key: "activity", header: "Last activity", render: (ch) => <span className={styles.muted}>{ch.live?.lastActivityAt ? formatDateTime(ch.live.lastActivityAt) : "—"}</span> },
    { key: "created", header: "Created", render: (ch) => <span className={styles.muted}>{formatDate(ch.createdAt)}</span> },
  ];

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <p className="u-eyebrow">Organization</p>
          <h1 className={styles.h1}>{org?.name ?? "…"}</h1>
        </div>
        {org && <Badge tone={STATUS_TONE[org.status]}>{org.status}</Badge>}
      </header>

      {query.isPending && (
        <div className={styles.loading}><Spinner size={20} color="var(--accent)" /> <span>Loading organization…</span></div>
      )}
      {query.isError && <Callout tone="danger" title="Couldn't load the organization">{apiMessage(query.error, "Please try again shortly.")}</Callout>}

      {org && (
        <>
          <Card className={styles.card}>
            <Detail label="Tier">{TIER_LABEL[org.tier]}{org.tierOverride ? " · operator override" : ""}</Detail>
            <Detail label="Subscription">{org.subscriptionStatus ?? "—"}</Detail>
            <Detail label="Channels" mono>{String(org.channels)}</Detail>
            <Detail label="Members" mono>{String(org.members)}</Detail>
            <Detail label="Created">{formatDate(org.createdAt)}</Detail>
            <Detail label="Org scope" mono>{org.orgScope}</Detail>
            <Detail label="Stripe customer" mono>{org.stripeCustomerId ?? "—"}</Detail>
          </Card>

          {/* Quota gauges (used vs tier limit) — doubles as the upsell/churn signal.
              Agents-used is the live relay count; "—" (unmetered) while it loads. */}
          <div className={styles.gauges}>
            <Card className={styles.gaugeCard}>
              <Gauge label="Channels" used={org.channels} limit={org.channelLimit} />
            </Card>
            <Card className={styles.gaugeCard}>
              <Gauge label="Agents (live)" used={agentsQuery.data ? agentsQuery.data.length : null} limit={org.agentLimit} />
            </Card>
          </div>

          <section className={styles.section}>
            <h2 className={styles.h2}>Admins</h2>
            <Card className={styles.card}>
              {org.admins.length === 0 ? (
                <p className={styles.muted}>No admins.</p>
              ) : (
                <ul className={styles.admins}>
                  {org.admins.map((a) => (
                    <li key={a.id} className={styles.adminRow}>
                      <span>{a.name ? `${a.name} · ` : ""}{a.email}</span>
                      <Badge tone={a.role === "owner" ? "accent" : "neutral"}>{a.role}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </section>

          <section className={styles.section}>
            <h2 className={styles.h2}>
              Agents{agentsQuery.data ? ` (${agentsQuery.data.length})` : ""}
            </h2>
            <Card className={styles.card}>
              {agentsQuery.isPending ? (
                <div className={styles.loading}><Spinner size={18} color="var(--accent)" /> <span>Loading agents…</span></div>
              ) : agentsQuery.isError ? (
                <Callout tone="danger">{apiMessage(agentsQuery.error, "Couldn't load agents.")}</Callout>
              ) : agentsQuery.data && agentsQuery.data.length > 0 ? (
                <AgentList agents={agentsQuery.data} />
              ) : (
                <p className={styles.muted}>No agents connected to this org's channels.</p>
              )}
            </Card>
          </section>

          <section className={styles.section}>
            <h2 className={styles.h2}>
              Channels{channelsQuery.data ? ` (${channelsQuery.data.length})` : ""}
            </h2>
            {channelsQuery.data && channelsQuery.data.length > 0 && (
              <div className={styles.rollup}>
                <Stat label="Live agents" value={String(liveAgents)} />
                <Stat label="Buffered messages" value={String(liveMessages)} />
                <Stat label="Last activity" value={lastActivityAt ? formatDateTime(lastActivityAt) : "—"} />
              </div>
            )}
            <Card className={styles.card}>
              {channelsQuery.isPending ? (
                <div className={styles.loading}><Spinner size={18} color="var(--accent)" /> <span>Loading channels…</span></div>
              ) : channelsQuery.isError ? (
                <Callout tone="danger">{apiMessage(channelsQuery.error, "Couldn't load channels.")}</Callout>
              ) : channelsQuery.data && channelsQuery.data.length > 0 ? (
                <Table columns={channelColumns} rows={channelsQuery.data} getRowKey={(ch) => ch.id} emptyText="No channels." />
              ) : (
                <p className={styles.muted}>No channels in this org.</p>
              )}
            </Card>
          </section>

          <section className={styles.section}>
            <h2 className={styles.h2}>Actions</h2>
            {status.isError && <Callout tone="danger">{apiMessage(status.error, "Couldn't change status.")}</Callout>}
            <div className={styles.actions}>
              {org.status === "active" && (
                <Button variant="danger" loading={status.isPending} onClick={() => status.mutate("suspended")}>Suspend</Button>
              )}
              {org.status === "suspended" && (
                <Button loading={status.isPending} onClick={() => status.mutate("active")}>Reactivate</Button>
              )}
              <Button variant="secondary" onClick={openTier}>Change tier</Button>
              <Button variant="secondary" onClick={openRefund}>Refund a charge</Button>
              {org.subscriptionStatus && (
                <Button variant="danger" onClick={openCancel}>Cancel subscription</Button>
              )}
              {org.tierOverride && (
                <Button variant="secondary" onClick={openRelease}>Release tier to billing</Button>
              )}
              <Button variant="secondary" loading={impersonate.isPending} onClick={() => impersonate.mutate()}>Impersonate for support</Button>
            </div>
            {impersonate.isError && <Callout tone="danger">{apiMessage(impersonate.error, "Couldn't start impersonation.")}</Callout>}

            {impersonation && (
              <Card className={styles.reveal}>
                <Callout tone="warning" title="Support impersonation token">
                  Acts as <strong>{impersonation.admin.email}</strong> for{" "}
                  {Math.round(impersonation.expiresInSeconds / 60)} minutes. Shown once — use it now.
                </Callout>
                <CopyField label="Bearer token" value={impersonation.token} multiline />
                <Button variant="ghost" size="sm" onClick={() => setImpersonation(null)}>Dismiss</Button>
              </Card>
            )}
          </section>
        </>
      )}

      {/* Refund modal */}
      <Modal open={refundOpen} onClose={closeRefund} title={refundResult ? "Refund issued" : "Refund a charge"} footer={refundResult ? <Button onClick={closeRefund}>Done</Button> : undefined}>
        {refundResult ? (
          <div className={styles.refundDone}>
            <Callout tone="success" title="Refund issued">
              Refund <code>{refundResult.refundId}</code> for {(refundResult.amount / 100).toFixed(2)} {refundResult.currency.toUpperCase()}{" "}
              ({refundResult.status ?? "pending"}).
            </Callout>
          </div>
        ) : (
          <RefundForm
            pending={refund.isPending}
            error={refund.isError ? apiMessage(refund.error, "Couldn't issue the refund.") : null}
            onSubmit={(v) => refund.mutate({ chargeId: v.chargeId, ...(v.amount ? { amount: Number(v.amount) } : {}) })}
          />
        )}
      </Modal>

      {/* Tier-change modal — note is required, saved for reconciliation. */}
      <Modal open={tierOpen} onClose={closeTier} title="Change tier">
        <TierForm
          currentTier={org?.tier ?? "free"}
          pending={tier.isPending}
          error={tier.isError ? apiMessage(tier.error, "Couldn't change the tier.") : null}
          onSubmit={(v) => tier.mutate(v)}
        />
      </Modal>

      {/* Cancel-subscription modal — at period end; note required, saved for reconciliation. */}
      <Modal
        open={cancelOpen}
        onClose={closeCancel}
        title={cancelResult ? "Cancellation scheduled" : "Cancel subscription"}
        footer={cancelResult ? <Button onClick={closeCancel}>Done</Button> : undefined}
      >
        {cancelResult ? (
          <Callout tone="success" title="Cancellation scheduled">
            The subscription will end
            {cancelResult.currentPeriodEnd ? ` on ${formatDate(cancelResult.currentPeriodEnd)}` : " at the end of the current period"} and
            the org will drop to Free then. (Stripe subscription <code>{cancelResult.subscriptionId}</code>)
          </Callout>
        ) : (
          <CancelForm
            pending={cancelSub.isPending}
            error={cancelSub.isError ? apiMessage(cancelSub.error, "Couldn't cancel the subscription.") : null}
            onSubmit={(v) => cancelSub.mutate(v)}
          />
        )}
      </Modal>

      {/* Release-to-billing modal — clears the operator tier override; note required. */}
      <Modal open={releaseOpen} onClose={closeRelease} title="Release tier to billing">
        <ReleaseForm
          pending={release.isPending}
          error={release.isError ? apiMessage(release.error, "Couldn't release the tier.") : null}
          onSubmit={(v) => release.mutate(v)}
        />
      </Modal>
    </div>
  );
}

function Detail({ label, mono, children }: { label: string; mono?: boolean; children: ReactNode }) {
  return (
    <div className={styles.detailRow}>
      <span className={styles.detailLabel}>{label}</span>
      <span className={mono ? styles.mono : undefined}>{children}</span>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.stat}>
      <span className={styles.statValue}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
    </div>
  );
}

const refundSchema = z.object({
  chargeId: z.string().trim().min(1, "A Stripe charge id is required"),
  // Amount in MINOR units (pence) — kept as a string field; blank = full refund.
  amount: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || /^\d+$/.test(v), "Amount must be a whole number of pence"),
});
type RefundValues = z.infer<typeof refundSchema>;

function RefundForm({ pending, error, onSubmit }: { pending: boolean; error: string | null; onSubmit: (v: RefundValues) => void }) {
  const { register, handleSubmit, formState: { errors } } = useForm<RefundValues>({ resolver: zodResolver(refundSchema) });
  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      {error && <div role="alert"><Callout tone="danger">{error}</Callout></div>}
      <TextField label="Stripe charge id" placeholder="ch_…" mono required error={errors.chargeId?.message} {...register("chargeId")} />
      <TextField label="Amount in pence (optional — blank = full refund)" type="number" placeholder="e.g. 1000" error={errors.amount?.message} {...register("amount")} />
      <Button type="submit" variant="danger" fullWidth loading={pending}>Issue refund</Button>
    </form>
  );
}

const tierFormSchema = z.object({
  tier: z.enum(["free", "small", "medium", "large", "xlarge"]),
  note: z.string().trim().min(3, "A note (min 3 characters) is required"),
});
type TierValues = z.infer<typeof tierFormSchema>;

export function TierForm({
  currentTier,
  pending,
  error,
  onSubmit,
}: {
  currentTier: OrgTier;
  pending: boolean;
  error: string | null;
  onSubmit: (v: TierValues) => void;
}) {
  const { register, handleSubmit, formState: { errors } } = useForm<TierValues>({
    resolver: zodResolver(tierFormSchema),
    defaultValues: { tier: currentTier, note: "" },
  });
  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      {error && <div role="alert"><Callout tone="danger">{error}</Callout></div>}
      <label className={styles.selectLabel}>
        <span>New tier</span>
        <select className={styles.select} {...register("tier")}>
          {TIERS.map((t) => (
            <option key={t} value={t}>{TIER_LABEL[t]}</option>
          ))}
        </select>
      </label>
      <TextField label="Note (required — saved for reconciliation)" placeholder="e.g. Q3 launch promo" required error={errors.note?.message} {...register("note")} />
      <Button type="submit" fullWidth loading={pending}>Change tier</Button>
    </form>
  );
}

const cancelFormSchema = z.object({
  note: z.string().trim().min(3, "A note (min 3 characters) is required"),
});
type CancelValues = z.infer<typeof cancelFormSchema>;

export function CancelForm({
  pending,
  error,
  onSubmit,
}: {
  pending: boolean;
  error: string | null;
  onSubmit: (v: CancelValues) => void;
}) {
  const { register, handleSubmit, formState: { errors } } = useForm<CancelValues>({ resolver: zodResolver(cancelFormSchema) });
  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      {error && <div role="alert"><Callout tone="danger">{error}</Callout></div>}
      <p className={styles.muted}>
        Cancels at period end — the org keeps access until the current period ends, then the billing
        webhook drops it to Free. No refund is issued (use "Refund a charge" for that).
      </p>
      <TextField label="Reason (required — saved for reconciliation)" placeholder="e.g. customer requested cancellation" required error={errors.note?.message} {...register("note")} />
      <Button type="submit" variant="danger" fullWidth loading={pending}>Cancel subscription</Button>
    </form>
  );
}

export function ReleaseForm({
  pending,
  error,
  onSubmit,
}: {
  pending: boolean;
  error: string | null;
  onSubmit: (v: CancelValues) => void;
}) {
  const { register, handleSubmit, formState: { errors } } = useForm<CancelValues>({ resolver: zodResolver(cancelFormSchema) });
  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      {error && <div role="alert"><Callout tone="danger">{error}</Callout></div>}
      <p className={styles.muted}>
        Hands the tier back to Stripe billing. If the org has no live subscription its tier drops to
        Free now; otherwise billing reconciles it on the next subscription change.
      </p>
      <TextField label="Reason (required — saved for reconciliation)" placeholder="e.g. end of promo period" required error={errors.note?.message} {...register("note")} />
      <Button type="submit" fullWidth loading={pending}>Release to billing</Button>
    </form>
  );
}
